import * as React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { isDefined } from '@klareospace/pharos-lib-utils';
import { Button } from '../../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';

export interface TablePaginationProps {
  currentPage: number;
  nextLabel: string;
  onPageChange: (page: number) => void;
  /** Fired when the user picks a different page size — provide with `pageSizeOptions` to show the selector. */
  onPageSizeChange?: (pageSize: number) => void;
  pageLabel: string;
  /** Currently selected page size, shown by the selector when `onPageSizeChange`/`pageSizeOptions` are provided. */
  pageSize?: number;
  /** Label rendered next to the page-size selector. */
  pageSizeLabel?: string;
  /** Selectable page-size values — provide with `onPageSizeChange` to show the selector. */
  pageSizeOptions?: number[];
  previousLabel: string;
  showingText?: string;
  totalPages: number;
}

interface PageSizeSelectorProps {
  label?: string;
  onPageSizeChange: (pageSize: number) => void;
  options: number[];
  value?: number;
}

function PageSizeSelector({ label, onPageSizeChange, options, value }: PageSizeSelectorProps): React.JSX.Element {
  function handleValueChange(next: string): void {
    onPageSizeChange(Number(next));
  }

  return (
    <div className="flex items-center gap-2">
      {label ? <span className="text-sm text-muted-foreground">{label}</span> : null}
      <Select onValueChange={handleValueChange} value={isDefined(value) ? String(value) : undefined}>
        <SelectTrigger aria-label={label} className="h-8 w-[76px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option} value={String(option)}>
              {option}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export function TablePagination({
  currentPage,
  nextLabel,
  onPageChange,
  onPageSizeChange,
  pageLabel,
  pageSize,
  pageSizeLabel,
  pageSizeOptions,
  previousLabel,
  showingText,
  totalPages,
}: TablePaginationProps): React.JSX.Element | null {
  const hasPageSizeSelector = isDefined(onPageSizeChange) && isDefined(pageSizeOptions);
  if (totalPages <= 1 && !hasPageSizeSelector) {
    return null;
  }

  return (
    <div className="flex flex-col items-center gap-2 border-t px-4 py-3 md:flex-row md:justify-between md:gap-0">
      {showingText ? <span className="text-sm text-muted-foreground">{showingText}</span> : null}
      <div className="flex items-center gap-4 md:ml-auto">
        {hasPageSizeSelector && (
          <PageSizeSelector
            label={pageSizeLabel}
            onPageSizeChange={onPageSizeChange}
            options={pageSizeOptions}
            value={pageSize}
          />
        )}
        {totalPages > 1 && (
          <div className="flex items-center gap-2">
            <Button
              disabled={currentPage === 1}
              onClick={(): void => onPageChange(currentPage - 1)}
              size="sm"
              variant="outline"
            >
              <ChevronLeft />
              {previousLabel}
            </Button>
            <span className="text-sm text-muted-foreground">{pageLabel}</span>
            <Button
              disabled={currentPage === totalPages}
              onClick={(): void => onPageChange(currentPage + 1)}
              size="sm"
              variant="outline"
            >
              {nextLabel}
              <ChevronRight />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
